"use client";

import { motion, useReducedMotion, type Variants } from "framer-motion";
import { marketingMotion } from "@/lib/motion/config";

interface IMotionRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  y?: number;
}

/**
 * Reusable in-view reveal for marketing content with reduced-motion fallback.
 */
export function MotionReveal({
  children,
  className,
  delay = 0,
  y = 16,
}: IMotionRevealProps): React.ReactElement {
  const prefersReducedMotion = useReducedMotion();

  const variants: Variants = prefersReducedMotion
    ? {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
      }
    : {
        hidden: { opacity: 0, y, scale: 0.985 },
        visible: {
          opacity: 1,
          y: 0,
          scale: 1,
          transition: {
            // Slight “pop” entrance with a short, bouncy spring.
            type: "spring",
            stiffness: 260,
            damping: 18,
            delay,
          },
        },
      };

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={marketingMotion.viewport}
      variants={variants}
    >
      {children}
    </motion.div>
  );
}
